import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Vote, VoteCreate, VoteDetail, VoteUpdate } from '@core/types/vote.type';
import { VoteService } from '@core/services';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class VoteQuery {
  private readonly service = inject(VoteService);
  private readonly queryClient = inject(QueryClient);

  useVotesGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.votes.grid(c),
        queryFn: () => lastValueFrom(this.service.grid(c)),
        select: (res: ApiResponse<PaginationResult<Vote>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useVoteDetail(id: () => string | null) {
    return injectQuery(() => {
      const v = id();
      return {
        queryKey: QUERY_KEYS.votes.detail(v || ''),
        queryFn: () => lastValueFrom(this.service.get(v!)),
        select: (res: ApiResponse<VoteDetail>) => res.data,
        enabled: !!v,
      };
    });
  }

  useCreateVote() {
    return injectMutation(() => ({
      mutationFn: (data: VoteCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.votes.all }),
    }));
  }

  useUpdateVote() {
    return injectMutation(() => ({
      mutationFn: (data: VoteUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.votes.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.votes.all });
      },
    }));
  }

  useDeleteVote() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.votes.all }),
    }));
  }
}

